import React from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface SystemsErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface SystemsErrorBoundaryProps {
  children: React.ReactNode;
}

export class SystemsErrorBoundary extends React.Component<SystemsErrorBoundaryProps, SystemsErrorBoundaryState> {
  constructor(props: SystemsErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): SystemsErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Systems page error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <Alert className="max-w-md">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Error en la página de sistemas</AlertTitle>
            <AlertDescription>
              Ha ocurrido un error al cargar la página de sistemas. Esto puede deberse a un problema de contexto o conexión.
              
              <div className="mt-4 space-y-2">
                <Button onClick={this.handleReset} variant="outline" size="sm" className="w-full">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Intentar de nuevo
                </Button>
                <Button onClick={() => window.location.href = '/clientes'} variant="default" size="sm" className="w-full">
                  Volver a Clientes
                </Button>
              </div>
              
              {this.state.error && (
                <details className="mt-2 text-xs">
                  <summary>Detalles técnicos</summary>
                  <pre className="mt-1 whitespace-pre-wrap text-xs">{this.state.error.message}</pre>
                </details>
              )}
            </AlertDescription>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}